import { useEffect, useMemo, useState } from 'react';
import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import { useDispatch } from 'react-redux';
import { addLine, removeLine, updateLine, calculateLineTotals } from 'store/budget/action';
import { useTranslation } from 'react-i18next';
import { Label } from 'common/components/texts/Texts';
import { Row, Col } from 'react-bootstrap';
import SearchableSelectInput from 'common/components/searchableSelectInput/SearchableSelectInput';
import { OptionTitleSubTitle, SearchableSelectTitleSubTitleOption } from 'common/components/searchableSelectInput/optionsFormats/OptionTitleSubTitle';
import FamiliesService from 'api/families/FamiliesService';
import ElementsService from 'api/elements/ElementsService';
import Logger from 'common/services/Logger';
import Toast from 'common/services/Toast';
import { LOGGER_LOG_TYPE, BODY_TOOLTIP } from 'Config';
import Button from 'common/components/button/Button';
import { IoTrash } from 'react-icons/io5';
import { FaExternalLinkAlt, FaPencilAlt } from 'react-icons/fa';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import Utils from 'common/services/Utils';
import BudgetsService from 'api/budgets/BudgetsService';
import { mapNewParametersToExisting } from '../../../utils';
import styles from './TreatmentsTab.module.scss';
import store from 'store/store';
import { BudgetData } from 'store/budget/type';

interface TreatmentEntry {
    elementId: string;
    designation: string;
    subDesignation: string;
    imageUrl?: string | null;
}

interface NewTreatmentRow {
    id: string;
    familyOption: SearchableSelectTitleSubTitleOption | null;
    elementOption: SearchableSelectTitleSubTitleOption | null;
}

interface Props {
    line: BudgetLineDto;
    disabled: boolean;
    isComponentElement: boolean;
}

function TreatmentsTab({ line, disabled, isComponentElement }: Props): JSX.Element | null {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [newRows, setNewRows] = useState<NewTreatmentRow[]>([]);
    const [isAdding, setIsAdding] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [removingElementId, setRemovingElementId] = useState<string | null>(null);
    const [editingTreatment, setEditingTreatment] = useState<{
        elementId: string;
        familyOption: SearchableSelectTitleSubTitleOption | null;
        elementOption: SearchableSelectTitleSubTitleOption | null;
    } | null>(null);

    const getLines = () => store.getState().budget.lines as BudgetLineDto[];
    const [componentFamilyId, setComponentFamilyId] = useState<string | null>(null);

    useEffect(() => {
        if (!isComponentElement) return;
        const allLines = getLines();
        const section = allLines.find(l =>
            l.parentKey === line.key
            && l.lineType === BudgetLineType.SECTION
            && l.sourceFamilyParameterId
        );
        if (section?.sourceFamilyParameterId) {
            FamiliesService.getFamilyIdByParameter(section.sourceFamilyParameterId)
                .then(fId => { if (fId) setComponentFamilyId(fId); })
                .catch(() => {});
        }
    }, [line.key, isComponentElement]);

    // Find all target element lines where treatments should be applied
    const { targetElementLines, currentTreatments } = useMemo(() => {
        const allLines = getLines();
        if (isComponentElement) {
            const sections = allLines.filter(l =>
                l.parentKey === line.key
                && l.lineType === BudgetLineType.SECTION
                && l.sourceRequiresTreatment
            );
            const elements = allLines.filter(l =>
                sections.some(s => s.key === l.parentKey)
                && (l.lineType === BudgetLineType.ELEMENT || l.lineType === BudgetLineType.COMPONENT_ELEMENT)
            );
            const treatmentMap = new Map<string, TreatmentEntry>();
            for (const elem of elements) {
                const children = allLines.filter(l =>
                    l.parentKey === elem.key
                    && (l.lineType === BudgetLineType.ELEMENT || l.lineType === BudgetLineType.COMPONENT_ELEMENT)
                    && l.elementId
                );
                for (const child of children) {
                    if (child.elementId && !treatmentMap.has(child.elementId)) {
                        treatmentMap.set(child.elementId, {
                            elementId: child.elementId,
                            designation: child.designation,
                            subDesignation: child.subDesignation ?? '',
                            imageUrl: child.imageUrl,
                        });
                    }
                }
            }
            return {
                targetElementLines: elements,
                currentTreatments: Array.from(treatmentMap.values()),
            };
        } else {
            const children = allLines.filter(l =>
                l.parentKey === line.key
                && (l.lineType === BudgetLineType.ELEMENT || l.lineType === BudgetLineType.COMPONENT_ELEMENT)
                && l.elementId
            );
            const treatmentMap = new Map<string, TreatmentEntry>();
            for (const child of children) {
                if (child.elementId && !treatmentMap.has(child.elementId)) {
                    treatmentMap.set(child.elementId, {
                        elementId: child.elementId,
                        designation: child.designation,
                        subDesignation: child.subDesignation ?? '',
                        imageUrl: child.imageUrl,
                    });
                }
            }
            return {
                targetElementLines: [line],
                currentTreatments: Array.from(treatmentMap.values()),
            };
        }
    }, [line.key, isComponentElement, refreshKey]);

    const onLoadFamilies = (
        inputValue: string,
        callback: (options: SearchableSelectTitleSubTitleOption[], hasMore: boolean) => void,
        _isStart: boolean,
        page: number
    ) => {
        FamiliesService.getList({
            hasParentId: false,
            isElement: true,
            isComponent: false,
            itemsPerPage: 10,
            page,
            name: inputValue,
            includeFamiliesAndSubFamilies: true,
        }).then(result => {
            const options: SearchableSelectTitleSubTitleOption[] = result.items.map(item => ({
                label: item.name,
                subTitle: item.parentName ?? '',
                value: item.id,
            }));
            callback(options, result.currentPage < result.totalPages);
        }).catch((error) => {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get families', error);
            Toast.error(t('messages.error_load_info'));
        });
    };

    const seenElementIds = useMemo(() => new Set<string>(), []);

    const onLoadElements = (
        familyId: string | undefined,
        inputValue: string,
        callback: (options: SearchableSelectTitleSubTitleOption[], hasMore: boolean) => void,
        isStart: boolean,
        page: number
    ) => {
        if (isStart || page <= 1) {
            seenElementIds.clear();
        }
        ElementsService.getElementsCatalogByFamilyId({
            itemsPerPage: 10,
            page,
            name: inputValue,
            familyId: familyId || undefined,
            isElement: true,
            nameFilterIgnoreFamilyAndReference: true,
        }).then(result => {
            const options: SearchableSelectTitleSubTitleOption[] = [];
            for (const item of result.items) {
                const key = String(item.value).toLowerCase();
                if (!seenElementIds.has(key)) {
                    seenElementIds.add(key);
                    options.push({
                        label: item.name ?? item.label,
                        subTitle: (item as any).subTitle ?? '',
                        value: item.value,
                    });
                }
            }
            callback(options, result.currentPage < result.totalPages);
        }).catch((error) => {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get elements', error);
            Toast.error(t('messages.error_load_info'));
        });
    };

    const onAddNewRow = () => {
        setNewRows(prev => [...prev, {
            id: Utils.newGuid(),
            familyOption: null,
            elementOption: null,
        }]);
    };

    const onRemoveNewRow = (rowId: string) => {
        setNewRows(prev => prev.filter(r => r.id !== rowId));
    };

    const onChangeFamilyInRow = (rowId: string, option: SearchableSelectTitleSubTitleOption | null | undefined) => {
        setNewRows(prev => prev.map(r =>
            r.id === rowId ? { ...r, familyOption: option ?? null, elementOption: null } : r
        ));
    };

    const onChangeElementInRow = (rowId: string, option: SearchableSelectTitleSubTitleOption | null | undefined) => {
        setNewRows(prev => prev.map(r =>
            r.id === rowId ? { ...r, elementOption: option ?? null } : r
        ));
    };

    const onConfirmAddTreatment = async (row: NewTreatmentRow) => {
        if (!row.elementOption?.value) return;

        const elementId = row.elementOption.value;

        // Check if treatment already exists
        if (currentTreatments.some(ct => ct.elementId === elementId)) {
            return;
        }

        try {
            setIsAdding(true);
            const budget: BudgetData = store.getState().budget.budget;
            const parametersData = await BudgetsService.getElementParameters(elementId, false);
            if (isComponentElement) {
                // Add treatment to each element in each requiresTreatment section
                for (const targetElement of targetElementLines) {
                    const childKey = Utils.newGuid();
                    dispatch(addLine(BudgetLineType.ELEMENT, 1, targetElement.key, childKey));
                    dispatch(updateLine({
                        key: childKey,
                        imageUrl: parametersData.imageUrl,
                        designation: parametersData.name,
                        subDesignation: (row.elementOption as any)?.subTitle ?? '',
                        elementId: parametersData.elementId,
                        grossUnitPrice: parametersData.price ?? 0,
                        unitId: parametersData.unitId ?? null,
                        unitSymbol: parametersData.unitSymbol ?? null,
                        taxId: budget.defaultTaxId ? budget.defaultTaxId : (parametersData.taxId ?? null),
                        description: parametersData.description,
                        parameters: mapNewParametersToExisting(parametersData.parameters ?? [], []),
                        checkedForParameters: true,
                        pdfVisibility: parametersData.pdfVisibility,
                        changedParameters: true,
                    }));
                    dispatch(calculateLineTotals(childKey));
                }
            } else {
                // ELEMENT: add directly as child
                const childKey = Utils.newGuid();
                dispatch(addLine(BudgetLineType.ELEMENT, 1, line.key, childKey));
                dispatch(updateLine({
                    key: childKey,
                    imageUrl: parametersData.imageUrl,
                    designation: parametersData.name,
                    subDesignation: (row.elementOption as any)?.subTitle ?? '',
                    elementId: parametersData.elementId,
                    grossUnitPrice: parametersData.price ?? 0,
                    unitId: parametersData.unitId ?? null,
                    taxId: budget.defaultTaxId ? budget.defaultTaxId : (parametersData.taxId ?? null),
                    description: parametersData.description,
                    parameters: mapNewParametersToExisting(parametersData.parameters ?? [], []),
                    checkedForParameters: true,
                    pdfVisibility: parametersData.pdfVisibility,
                    changedParameters: true,
                }));
                dispatch(calculateLineTotals(childKey));
            }

            // Remove the row from new rows
            onRemoveNewRow(row.id);
            setRefreshKey(k => k + 1);
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t add treatment', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            setIsAdding(false);
        }
    };

    const onRemoveTreatment = (elementId: string) => {
        const allLines = getLines();
        if (isComponentElement) {
            // Remove from all target elements
            for (const targetElement of targetElementLines) {
                const treatmentLines = allLines.filter(l =>
                    l.parentKey === targetElement.key
                    && l.elementId === elementId
                );
                for (const treatmentLine of treatmentLines) {
                    dispatch(removeLine(treatmentLine.key));
                }
            }
        } else {
            // Remove direct children
            const treatmentLines = allLines.filter(l =>
                l.parentKey === line.key
                && l.elementId === elementId
            );
            for (const treatmentLine of treatmentLines) {
                dispatch(removeLine(treatmentLine.key));
            }
        }
        setRefreshKey(k => k + 1);
    };

    const onStartEdit = (treatment: TreatmentEntry) => {
        setEditingTreatment({
            elementId: treatment.elementId,
            familyOption: null,
            elementOption: null,
        });
        // Pre-select the family of the element being replaced
        if (treatment.elementId) {
            BudgetsService.getElementParameters(treatment.elementId, false).then(elementData => {
                if (!elementData.parameters?.length) return;
                const firstParam = elementData.parameters[0];
                if (!firstParam.familyParameterId) return;
                return FamiliesService.getFamilyIdByParameter(firstParam.familyParameterId).then(familyId => {
                    if (!familyId) return;
                    return FamiliesService.getById(familyId).then(family => {
                        setEditingTreatment(prev => prev && prev.elementId === treatment.elementId
                            ? {
                                ...prev,
                                familyOption: {
                                    value: family.id,
                                    label: family.name,
                                    subTitle: family.parentName ?? '',
                                },
                            }
                            : prev
                        );
                    });
                });
            }).catch(() => {});
        }
    };

    const onCancelEdit = () => {
        setEditingTreatment(null);
    };

    const onChangeEditFamily = (option: SearchableSelectTitleSubTitleOption | null | undefined) => {
        if (editingTreatment) {
            setEditingTreatment({ ...editingTreatment, familyOption: option ?? null, elementOption: null });
        }
    };

    const onChangeEditElement = (option: SearchableSelectTitleSubTitleOption | null | undefined) => {
        if (editingTreatment) {
            setEditingTreatment({ ...editingTreatment, elementOption: option ?? null });
        }
    };

    const onReplaceTreatment = async () => {
        if (!editingTreatment?.elementOption?.value) return;
        const oldElementId = editingTreatment.elementId;
        const newElementId = editingTreatment.elementOption.value;
        if (oldElementId === newElementId) {
            onCancelEdit();
            return;
        }

        try {
            setIsAdding(true);
            const budget: BudgetData = store.getState().budget.budget;
            const parametersData = await BudgetsService.getElementParameters(newElementId, false);
            const allLines = getLines();

            if (isComponentElement) {
                for (const targetElement of targetElementLines) {
                    const treatmentLines = allLines.filter(l =>
                        l.parentKey === targetElement.key && l.elementId === oldElementId
                    );
                    for (const treatmentLine of treatmentLines) {
                        dispatch(updateLine({
                            key: treatmentLine.key,
                            imageUrl: parametersData.imageUrl,
                            designation: parametersData.name,
                            subDesignation: editingTreatment.elementOption?.label ?? '',
                            elementId: parametersData.elementId,
                            grossUnitPrice: parametersData.price ?? 0,
                            unitId: parametersData.unitId ?? null,
                        unitSymbol: parametersData.unitSymbol ?? null,
                            taxId: budget.defaultTaxId ? budget.defaultTaxId : (parametersData.taxId ?? null),
                            description: parametersData.description,
                            parameters: mapNewParametersToExisting(parametersData.parameters ?? [], []),
                            checkedForParameters: true,
                            pdfVisibility: parametersData.pdfVisibility,
                            changedParameters: true,
                        }));
                        dispatch(calculateLineTotals(treatmentLine.key));
                    }
                }
            } else {
                const treatmentLines = allLines.filter(l =>
                    l.parentKey === line.key && l.elementId === oldElementId
                );
                for (const treatmentLine of treatmentLines) {
                    dispatch(updateLine({
                        key: treatmentLine.key,
                        imageUrl: parametersData.imageUrl,
                        designation: parametersData.name,
                        subDesignation: editingTreatment.elementOption?.label ?? '',
                        elementId: parametersData.elementId,
                        grossUnitPrice: parametersData.price ?? 0,
                        unitId: parametersData.unitId ?? null,
                        unitSymbol: parametersData.unitSymbol ?? null,
                        taxId: budget.defaultTaxId ? budget.defaultTaxId : (parametersData.taxId ?? null),
                        description: parametersData.description,
                        parameters: mapNewParametersToExisting(parametersData.parameters ?? [], []),
                        checkedForParameters: true,
                        pdfVisibility: parametersData.pdfVisibility,
                        changedParameters: true,
                    }));
                    dispatch(calculateLineTotals(treatmentLine.key));
                }
            }

            setEditingTreatment(null);
            setRefreshKey(k => k + 1);
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t replace treatment', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            setIsAdding(false);
        }
    };

    return (
        <div className={styles.container}>
            {!disabled && (
                <div className={styles.helpText}>
                    {t(isComponentElement ? 'budgets.budget.lines.tabs.treatments_help' : 'budgets.budget.lines.tabs.treatments_help_element')}
                    {isComponentElement && componentFamilyId && (
                        <FaExternalLinkAlt
                            className={styles.familyLink}
                            onClick={() => window.open('/library/components/families/details/' + componentFamilyId, '_blank')}
                            data-tooltip-id={BODY_TOOLTIP}
                            data-tooltip-content={t('budgets.budget.lines.tabs.treatments_open_family')}
                        />
                    )}
                </div>
            )}
            {/* Existing treatments */}
            {currentTreatments.map((treatment) => (
                <div key={treatment.elementId}>
                    {editingTreatment?.elementId === treatment.elementId
                        ? (
                        <Row className={`${styles.treatmentRow} align-items-end`}>
                            <Col xs={12} sm={4}>
                                <Label space>{t('budgets.budget.lines.element_family_subfamily')}</Label>
                                <SearchableSelectInput
                                    value={editingTreatment.familyOption?.value}
                                    loadOptions={onLoadFamilies}
                                    onChange={(v, opt) => onChangeEditFamily(opt)}
                                    formatOptionLabel={OptionTitleSubTitle}
                                    noFilter={true}
                                />
                            </Col>
                            <Col xs={12} sm>
                                <Label space>{t('budgets.budget.lines.element')}{'*'}</Label>
                                <SearchableSelectInput
                                    key={editingTreatment.familyOption?.value ?? 'no-family'}
                                    value={editingTreatment.elementOption?.value}
                                    loadOptions={(inputValue, cb, isStart, page) =>
                                        onLoadElements(editingTreatment.familyOption?.value, inputValue, cb, isStart, page)
                                    }
                                    onChange={(v, opt) => onChangeEditElement(opt)}
                                    formatOptionLabel={OptionTitleSubTitle}
                                    noFilter={true}
                                />
                            </Col>
                            <Col xs={'auto'} className="d-flex gap-2" style={{ marginBottom: '4px' }}>
                                <Button
                                    size="small"
                                    disabled={!editingTreatment.elementOption || isAdding}
                                    onClick={onReplaceTreatment}
                                >
                                    {t('common.ok')}
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="small"
                                    onClick={onCancelEdit}
                                >
                                    {t('common.cancel')}
                                </Button>
                            </Col>
                        </Row>
                        )
                        : (
                        <div className={styles.existingTreatment}>
                            {!disabled && (
                                <div className={styles.treatmentActions}>
                                    <IoTrash
                                        className={styles.removeIcon}
                                        onClick={() => setRemovingElementId(treatment.elementId)}
                                        data-tooltip-id={BODY_TOOLTIP}
                                        data-tooltip-content={t('budgets.budget.lines.tabs.treatments_remove')}
                                    />
                                    <FaPencilAlt
                                        className={styles.editIcon}
                                        onClick={() => onStartEdit(treatment)}
                                        data-tooltip-id={BODY_TOOLTIP}
                                        data-tooltip-content={t('budgets.budget.lines.tabs.treatments_replace')}
                                    />
                                </div>
                            )}
                            <div className={styles.treatmentInfo}>
                                <span className={styles.treatmentName}>{treatment.designation}</span>
                                {treatment.subDesignation && (
                                    <span className={styles.treatmentFamily}>{treatment.subDesignation}</span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            ))}

            {/* New treatment rows */}
            {newRows.map((row) => (
                <Row key={row.id} className={`${styles.treatmentRow} align-items-end`}>
                    <Col xs={12} sm={4}>
                        <Label space>{t('budgets.budget.lines.element_family_subfamily')}</Label>
                        <SearchableSelectInput
                            value={row.familyOption?.value}
                            loadOptions={onLoadFamilies}
                            onChange={(v, opt) => onChangeFamilyInRow(row.id, opt)}
                            formatOptionLabel={OptionTitleSubTitle}
                            noFilter={true}
                        />
                    </Col>
                    <Col xs={12} sm>
                        <Label space>{t('budgets.budget.lines.element')}{'*'}</Label>
                        <SearchableSelectInput
                            key={row.familyOption?.value ?? 'no-family'}
                            value={row.elementOption?.value}
                            loadOptions={(inputValue, cb, isStart, page) =>
                                onLoadElements(row.familyOption?.value, inputValue, cb, isStart, page)
                            }
                            onChange={(v, opt) => onChangeElementInRow(row.id, opt)}
                            formatOptionLabel={OptionTitleSubTitle}
                            noFilter={true}
                        />
                    </Col>
                    <Col xs={'auto'} className="d-flex gap-2" style={{ marginBottom: '4px' }}>
                        <Button
                            size="small"
                            disabled={!row.elementOption || isAdding}
                            onClick={() => onConfirmAddTreatment(row)}
                        >
                            {t('common.ok')}
                        </Button>
                        <Button
                            variant="secondary"
                            size="small"
                            onClick={() => onRemoveNewRow(row.id)}
                        >
                            {t('common.remove')}
                        </Button>
                    </Col>
                </Row>
            ))}

            {/* Add button */}
            {!disabled && (
                <div className={styles.addButton}>
                    <Button variant="secondary" size="small" onClick={onAddNewRow}>
                        + {t('common.new')}
                    </Button>
                </div>
            )}

            <ConfirmDeleteModal
                isOpen={removingElementId !== null}
                message={t(isComponentElement ? 'budgets.budget.lines.tabs.treatments_remove_confirm' : 'budgets.budget.lines.tabs.treatments_remove_confirm_element')}
                onClose={(confirmed) => {
                    if (confirmed && removingElementId) {
                        onRemoveTreatment(removingElementId);
                    }
                    setRemovingElementId(null);
                }}
            />
        </div>
    );
}

export default TreatmentsTab;
